import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Image,
  Keyboard,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Icon, { IconName } from '../components/ui/Icon';
import Text from '../components/ui/Text';
import { Booking } from '../data/bookings';
import { Category, CategoryKey } from '../data/services';
import { useChat } from '../hooks/useChat';
import { ChatMessageRow } from '../services/chat';
import {
  colors,
  hairline,
  inputText,
  radii,
  screenPadding,
  shadows,
  spacing,
  useResponsive,
} from '../theme';

/**
 * Messages with the professionals doing your work.
 *
 * Two views in one screen - the list of conversations, and one conversation -
 * rather than two screens, because there is no navigator and the second is
 * always reached from the first. `open` is the whole routing story.
 *
 * ---------------------------------------------------------------------------
 * A THREAD IS A BOOKING WITH SOMEONE ASSIGNED
 * ---------------------------------------------------------------------------
 * There is no separate conversation list on the server - the booking id IS
 * the thread key (`GET/POST /bookings/:id/messages`, see `services/chat.ts`).
 * So `bookings` (the real, live list - never the seeded examples; a booking
 * with no `serviceId` has never touched the platform and has nothing to
 * fetch) is filtered down to the ones with a professional on them, and that
 * is the whole list. Support has no thread here - see `onPressNotifications`
 * / the account's own Help & Support page for that.
 *
 * Each conversation is polled, not pushed - see `useChat` for why.
 */

type ChatThread = {
  bookingId: string;
  name: string;
  about: string;
  category: CategoryKey;
  /** Live work is pinned above finished work. */
  active: boolean;
};

type Props = {
  categoryFor: (key: CategoryKey) => Category;
  /** The real, live bookings list - platform bookings only. */
  bookings: Booking[];
  /**
   * Open straight on this booking's conversation instead of the list.
   *
   * Set when the screen is reached from a booking rather than from the tab.
   * `onExit` goes with it: back from a thread opened this way belongs at the
   * booking it came from, not at a list the user never saw.
   */
  initialBooking?: Booking;
  onExit?: () => void;
  /**
   * Back from the list itself, when it was opened from somewhere rather than
   * picked as a tab - Help & Support's "Chat with us". Omitted, the list is a
   * tab root and draws no arrow.
   */
  onBack?: () => void;
  /** Raises the shared notifications sheet, the same one Home's bell opens. */
  onPressNotifications?: () => void;
  /** Unread notifications, for the dot on the bell. */
  notificationCount?: number;
};

/** The chips over the list. `all` first, because it is the way back. */
type ChatFilter = 'all' | 'live';
const FILTERS: Array<{ key: ChatFilter; label: string; icon?: IconName }> = [
  { key: 'all', label: 'All' },
  { key: 'live', label: 'Live', icon: 'bolt' },
];

/** Intrinsic size of the banner, so the block keeps its exact aspect. */
const HERO_ASPECT = 2172 / 724;

/** The back disc over the banner - the bell's size, in the other corner. */
const BACK_SIZE = 38;

const threadFor = (booking: Booking): ChatThread | undefined => {
  if (!booking.professional) {
    return undefined;
  }
  return {
    bookingId: booking.id,
    name: booking.professional.name,
    about: booking.title,
    category: booking.category,
    active: booking.status === 'live',
  };
};

/** "09:41" from an ISO instant - there is no clock to compute against otherwise. */
const timeOf = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleTimeString('en-IN', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

// -------------------------------------------------------------- small parts

function Avatar({ name, category }: { name: string; category: Category }) {
  return (
    <View style={styles.avatar}>
      <Icon name={category.icon} size={18} color={colors.primary} />
      {/* The initial rides the corner, so two professionals in the same trade
          are still distinguishable at a glance. */}
      <View style={styles.avatarInitial}>
        <Text variant="caption" color={colors.onPrimary} style={styles.initialText}>
          {name.charAt(0)}
        </Text>
      </View>
    </View>
  );
}

function Bubble({
  message,
  maxWidth,
}: {
  message: ChatMessageRow;
  /**
   * Longest line the bubble may run to, in points.
   *
   * A percentage alone is not enough. 78% of a phone is a bubble; 78% of a
   * tablet is a 700pt line, and past roughly 60 characters the eye loses the
   * start of the next line - the one thing a chat transcript cannot afford.
   * The percentage still applies underneath this, so on a phone nothing
   * changes and the cap never binds.
   */
  maxWidth: number;
}) {
  const mine = message.senderType === 'customer';

  return (
    <View style={[styles.bubbleRow, mine ? styles.bubbleRowMine : null]}>
      <View
        style={[
          styles.bubble,
          { maxWidth },
          mine ? styles.bubbleMine : styles.bubbleTheirs,
        ]}
      >
        <Text
          variant="bodySm"
          color={mine ? colors.inkInverse : colors.inkBody}
        >
          {message.body}
        </Text>
        <Text
          variant="caption"
          color={mine ? colors.inkInverseMuted : colors.inkMuted}
          style={styles.stamp}
        >
          {timeOf(message.sentAt)}
        </Text>
      </View>
    </View>
  );
}

/**
 * Longest a message bubble may run, in points - see `Bubble`'s `maxWidth`.
 *
 * 420 is about sixty characters at this app's body size, which is the width
 * printed text has used for four centuries for exactly this reason.
 */
const BUBBLE_MAX = 420;

// ---------------------------------------------------------------- the thread

function Thread({
  booking,
  thread,
  category,
  resp,
  insets,
  onBack,
}: {
  booking: Booking;
  thread: ChatThread;
  category: Category;
  resp: ReturnType<typeof useResponsive>;
  insets: { bottom: number };
  onBack: () => void;
}) {
  const chat = useChat(booking.id);
  const [text, setText] = useState('');
  const [keyboardUp, setKeyboardUp] = useState(false);
  const scrollRef = useRef<ScrollView>(null);

  useEffect(() => {
    const ios = Platform.OS === 'ios';
    const show = Keyboard.addListener(
      ios ? 'keyboardWillShow' : 'keyboardDidShow',
      () => setKeyboardUp(true),
    );
    const hide = Keyboard.addListener(
      ios ? 'keyboardWillHide' : 'keyboardDidHide',
      () => setKeyboardUp(false),
    );
    return () => {
      show.remove();
      hide.remove();
    };
  }, []);

  const send = async () => {
    const body = text.trim();
    if (!body || chat.sending) {
      return;
    }
    setText('');
    try {
      await chat.send(body);
      requestAnimationFrame(() => scrollRef.current?.scrollToEnd({ animated: true }));
    } catch {
      // `chat.error` is not set from `send` failures on purpose - the
      // composer keeps the customer's unsent text below rather than losing
      // it, so put it back and let them retry.
      setText(body);
    }
  };

  return (
    <KeyboardAvoidingView
      style={styles.root}
      /*
       * Both platforms need a behavior, not just iOS.
       *
       * `undefined` on Android used to mean no keyboard avoidance at all -
       * the same gap that let the keyboard cover the note field on
       * `LocationScreen`. `'height'` is the Android-appropriate strategy:
       * it shrinks this view by the keyboard's height rather than padding
       * around it, which is what `'padding'` does on iOS.
       */
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <View
        style={[
          styles.header,
          /* Pinned above the thread, so it centres on the column by hand. */
          { paddingHorizontal: screenPadding + resp.sideInset },
        ]}
      >
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Back"
          onPress={onBack}
          hitSlop={12}
        >
          <Icon name="chevronLeft" size={22} color={colors.ink} />
        </Pressable>

        <Avatar name={thread.name} category={category} />

        <View style={styles.headerText}>
          <Text variant="subtitle" numberOfLines={1}>
            {thread.name}
          </Text>
          <Text variant="caption" numberOfLines={1} color={colors.inkMuted}>
            {thread.active ? 'On the way · ' : ''}
            {thread.about}
          </Text>
        </View>
      </View>

      <ScrollView
        ref={scrollRef}
        /* Zero on a phone; a centred column on a tablet. */
        contentContainerStyle={[styles.thread, resp.column]}
        showsVerticalScrollIndicator={false}
        onContentSizeChange={() => scrollRef.current?.scrollToEnd({ animated: false })}
      >
        {chat.loading ? (
          <Text variant="caption" align="center" color={colors.inkSubtle}>
            Loading messages…
          </Text>
        ) : chat.error ? (
          <Text variant="caption" align="center" color={colors.danger}>
            {chat.error}
          </Text>
        ) : chat.messages.length === 0 ? (
          <Text variant="caption" align="center" color={colors.inkSubtle}>
            Say hello - {thread.name} will see it here.
          </Text>
        ) : (
          chat.messages.map(message => (
            <Bubble
              key={message.id}
              message={message}
              maxWidth={Math.min(resp.contentWidth * 0.78, BUBBLE_MAX)}
            />
          ))
        )}
      </ScrollView>

      {chat.closed ? (
        <View
          style={[
            styles.composer,
            {
              paddingHorizontal: screenPadding + resp.sideInset,
              paddingBottom: Math.max(insets.bottom, spacing.md),
            },
          ]}
        >
          <Text variant="caption" color={colors.inkMuted} style={styles.grow}>
            This conversation is closed. You can still read it, but new
            messages can no longer be sent.
          </Text>
        </View>
      ) : (
        <View
          style={[
            styles.composer,
            {
              paddingHorizontal: screenPadding + resp.sideInset,
              /*
               * The gesture bar / home indicator, not just a fixed padding -
               * and only while the keyboard is down.
               */
              paddingBottom: keyboardUp
                ? spacing.md
                : Math.max(insets.bottom, spacing.md),
            },
          ]}
        >
          <TextInput
            value={text}
            onChangeText={setText}
            placeholder="Write a message"
            placeholderTextColor={colors.inkSubtle}
            style={styles.input}
            multiline
            editable={!chat.sending}
            accessibilityLabel="Message"
          />
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Send"
            accessibilityState={{ disabled: text.trim().length === 0 || chat.sending }}
            disabled={text.trim().length === 0 || chat.sending}
            onPress={send}
            style={({ pressed }) => [
              styles.send,
              text.trim().length === 0 || chat.sending ? styles.sendOff : null,
              pressed ? styles.pressed : null,
            ]}
          >
            <Icon
              name="arrowRight"
              size={18}
              color={colors.onPrimary}
              strokeWidth={2.2}
            />
          </Pressable>
        </View>
      )}
    </KeyboardAvoidingView>
  );
}

// ------------------------------------------------------------------- screen

function ChatScreen({
  categoryFor,
  bookings,
  initialBooking,
  onExit,
  onBack,
  onPressNotifications,
  notificationCount = 0,
}: Props) {
  const resp = useResponsive();
  const insets = useSafeAreaInsets();

  const [openBooking, setOpenBooking] = useState<Booking | null>(
    initialBooking ?? null,
  );
  const [filter, setFilter] = useState<ChatFilter>('all');
  const [query, setQuery] = useState('');

  const threaded = useMemo(
    () =>
      bookings
        .map(booking => {
          const thread = threadFor(booking);
          return thread ? { booking, thread } : null;
        })
        .filter((row): row is { booking: Booking; thread: ChatThread } => row !== null)
        // Live work first: it is the only conversation with anything at stake.
        .sort((a, b) => Number(b.thread.active) - Number(a.thread.active)),
    [bookings],
  );

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return threaded.filter(({ thread }) => {
      if (filter === 'live' && !thread.active) {
        return false;
      }
      if (!needle) {
        return true;
      }
      return (
        thread.name.toLowerCase().includes(needle) ||
        thread.about.toLowerCase().includes(needle)
      );
    });
  }, [threaded, filter, query]);

  const open = openBooking ? threadFor(openBooking) : null;

  // ------------------------------------------------------------ one thread

  if (openBooking && open) {
    return (
      <Thread
        booking={openBooking}
        thread={open}
        category={categoryFor(open.category)}
        resp={resp}
        insets={insets}
        onBack={() => (onExit ? onExit() : setOpenBooking(null))}
      />
    );
  }

  // ------------------------------------------------------- the conversations

  return (
    <View style={styles.root}>
      <ScrollView
        style={styles.grow}
        /* Zero on a phone; a centred column on a tablet. */
        contentContainerStyle={[styles.page, resp.column]}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {/*
          The banner is the header, not a picture above one.

          Its own aspect drives the height, so the artwork is never cropped - the
          figure sits in the right third and the copy goes over the empty ground
          on the left, which is what the illustration was drawn for.
        */}
        <View style={[styles.hero, onBack ? styles.heroUnderBack : null]}>
          <Image
            source={require('../../assets/uploads/chat/chat.png')}
            style={styles.heroArt}
            resizeMode="cover"
          />

          <View style={styles.heroCopy} pointerEvents="none">
            <Text variant="titleLg" style={styles.heroTitle}>
              Chats
            </Text>
            <Text variant="caption" color={colors.inkBody}>
              Stay connected with your professionals anytime.
            </Text>
          </View>

          <Pressable
            accessibilityRole="button"
            accessibilityLabel={
              notificationCount > 0
                ? `Notifications, ${notificationCount} unread`
                : 'Notifications'
            }
            onPress={onPressNotifications}
            hitSlop={10}
            style={({ pressed }) => [styles.bell, pressed ? styles.pressed : null]}
          >
            <Icon name="bell" size={19} color={colors.ink} strokeWidth={1.8} />
            {/* A dot, not a count: the number is in the sheet the bell opens, and
                at this size two digits would not fit anyway. */}
            {notificationCount > 0 ? <View style={styles.bellDot} /> : null}
          </Pressable>
        </View>

        <View style={styles.search}>
          <Icon name="search" size={18} color={colors.inkMuted} />
          <TextInput
            value={query}
            onChangeText={setQuery}
            placeholder="Search chats"
            placeholderTextColor={colors.inkSubtle}
            style={styles.searchInput}
            accessibilityLabel="Search chats"
            returnKeyType="search"
          />
          {query ? (
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Clear search"
              onPress={() => setQuery('')}
              hitSlop={10}
            >
              {/* The icon set has no cross; a rotated plus is the same shape. */}
              <Icon
                name="plus"
                size={17}
                color={colors.inkMuted}
                style={styles.clear}
              />
            </Pressable>
          ) : null}
        </View>

        {/*
          `flexGrow: 0` on the rail, and the chips centred inside it.

          A horizontal ScrollView still stretches its content on the cross axis,
          and with no height of its own it claims whatever vertical space is
          going - so each chip grew to fill the screen the moment the row was
          given room, and the labels clipped on the way there. The rail hugs its
          chips; `alignItems` stops them stretching to its height.
        */}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.chipRail}
          /* Outside the page's column, so it carries its own inset. */
          contentContainerStyle={[styles.chips, resp.page]}
        >
          {FILTERS.map(item => {
            const on = item.key === filter;
            return (
              <Pressable
                key={item.key}
                accessibilityRole="button"
                accessibilityState={{ selected: on }}
                accessibilityLabel={item.label}
                onPress={() => setFilter(item.key)}
                style={({ pressed }) => [
                  styles.chip,
                  on ? styles.chipOn : null,
                  pressed ? styles.pressed : null,
                ]}
              >
                {item.icon ? (
                  <Icon
                    name={item.icon}
                    size={15}
                    color={on ? colors.onPrimary : colors.primary}
                  />
                ) : null}
                <Text
                  variant="labelSm"
                  color={on ? colors.onPrimary : colors.inkBody}
                >
                  {item.label}
                </Text>
              </Pressable>
            );
          })}
        </ScrollView>

        <View style={styles.listHead}>
          <Text variant="subtitle" style={styles.listHeadText}>
            Messages
          </Text>
          <Text variant="caption" color={colors.inkMuted}>
            {shown.length} {shown.length === 1 ? 'chat' : 'chats'}
          </Text>
        </View>

        {shown.length === 0 ? (
          <View style={styles.empty}>
            <Icon name="chat" size={34} color={colors.borderStrong} />
            <Text variant="subtitle" align="center">
              {threaded.length === 0 ? 'No conversations yet' : 'Nothing here'}
            </Text>
            <Text variant="bodySm" align="center" color={colors.inkMuted}>
              {threaded.length === 0
                ? 'Once a professional is assigned to a booking, you can message them here.'
                : 'No chat matches that filter or search.'}
            </Text>
          </View>
        ) : (
          shown.map(({ booking, thread }) => (
            <Pressable
              key={thread.bookingId}
              accessibilityRole="button"
              accessibilityLabel={`Conversation with ${thread.name} about ${thread.about}`}
              onPress={() => setOpenBooking(booking)}
              style={({ pressed }) => [styles.row, pressed ? styles.pressed : null]}
            >
              <View>
                <Avatar name={thread.name} category={categoryFor(thread.category)} />
                {/* Live work gets a dot on the avatar rather than a word in the
                    row - the row is already two lines of text. */}
                {thread.active ? <View style={styles.live} /> : null}
              </View>

              <View style={styles.rowText}>
                <View style={styles.rowTop}>
                  <Text variant="subtitle" numberOfLines={1} style={styles.grow}>
                    {thread.name}
                  </Text>
                </View>

                <View style={styles.rowTop}>
                  <Text
                    variant="bodySm"
                    color={colors.inkMuted}
                    numberOfLines={1}
                    style={styles.grow}
                  >
                    {thread.about}
                  </Text>
                </View>
              </View>
            </Pressable>
          ))
        )}
      </ScrollView>

      {/*
        Outside the scroll view, so it stays on screen however far the list
        runs - the bell beside the title scrolls away, and that is survivable;
        the only way back to Help & Support scrolling away is not.
      */}
      {onBack ? (
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Back"
          onPress={onBack}
          hitSlop={10}
          style={({ pressed }) => [
            styles.back,
            { left: screenPadding + resp.sideInset },
            pressed ? styles.pressed : null,
          ]}
        >
          <Icon name="chevronLeft" size={22} color={colors.ink} />
        </Pressable>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background,
  },

  page: {
    // Clears the floating tab bar, which overlays the bottom of the screen.
    paddingBottom: 120,
  },

  // --------------------------------------------------------------- the hero
  hero: {
    width: '100%',
    aspectRatio: HERO_ASPECT,
    justifyContent: 'center',
    overflow: 'hidden',
  },
  heroArt: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
  /* Held to the left half. The illustration fills the right, and copy run any
     wider would set over the figure rather than beside it. */
  heroCopy: {
    maxWidth: '52%',
    paddingLeft: screenPadding,
    gap: 2,
  },
  /* The weight lives in `titleLg` now. */
  heroTitle: {},
  /* Drops the centred copy clear of the back disc in the top corner, which
     would otherwise sit on the title. */
  heroUnderBack: {
    paddingTop: spacing.md + BACK_SIZE,
  },
  back: {
    position: 'absolute',
    top: spacing.md,
    width: BACK_SIZE,
    height: BACK_SIZE,
    borderRadius: BACK_SIZE / 2,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surface,
    ...shadows.sm,
  },
  bell: {
    position: 'absolute',
    top: spacing.md,
    right: screenPadding,
    width: 38,
    height: 38,
    borderRadius: 19,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surface,
    ...shadows.sm,
  },
  bellDot: {
    position: 'absolute',
    top: 8,
    right: 9,
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.danger,
    borderWidth: 1.5,
    borderColor: colors.surface,
  },

  // ------------------------------------------------------- search and chips
  search: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginHorizontal: screenPadding,
    marginTop: spacing.lg,
    paddingHorizontal: spacing.md,
    height: 48,
    borderRadius: radii.md,
    backgroundColor: colors.surfaceMuted,
  },
  searchInput: {
    flex: 1,
    ...inputText,
  },
  /* The icon set has no cross; a rotated plus is the same shape. */
  clear: {
    transform: [{ rotate: '45deg' }],
  },
  chipRail: {
    flexGrow: 0,
  },
  chips: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: screenPadding,
    paddingVertical: spacing.md,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderRadius: radii.pill,
    backgroundColor: colors.surface,
    ...hairline,
  },
  chipOn: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },

  // --------------------------------------------------------------- the list
  listHead: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: screenPadding,
    paddingTop: spacing.xs,
    paddingBottom: spacing.sm,
  },
  listHeadText: {
    flex: 1,
    fontWeight: '700',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    marginHorizontal: screenPadding,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  rowText: {
    flex: 1,
    gap: 3,
  },
  rowTop: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  grow: {
    flex: 1,
  },
  /* On the avatar, not in the row: it says "this one is happening now" without
     spending a line of the row on saying so. */
  live: {
    position: 'absolute',
    right: 0,
    bottom: 0,
    width: 11,
    height: 11,
    borderRadius: 6,
    backgroundColor: colors.success,
    borderWidth: 2,
    borderColor: colors.background,
  },

  avatar: {
    width: 42,
    height: 42,
    borderRadius: 21,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAccent,
  },
  avatarInitial: {
    position: 'absolute',
    right: -2,
    bottom: -2,
    width: 18,
    height: 18,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.primary,
  },
  initialText: {
    fontSize: 10,
    lineHeight: 13,
  },

  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingHorizontal: screenPadding,
    paddingTop: spacing.sm,
    paddingBottom: spacing.md,
    backgroundColor: colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  headerText: {
    flex: 1,
  },

  thread: {
    padding: screenPadding,
    gap: spacing.sm,
  },
  bubbleRow: {
    flexDirection: 'row',
  },
  bubbleRowMine: {
    justifyContent: 'flex-end',
  },
  /*
   * Capped at 78% so a long message still leaves the opposite margin visible.
   * Without it a full-width bubble reads as a paragraph rather than as one
   * side of a conversation.
   */
  bubble: {
    maxWidth: '78%',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: radii.md,
  },
  bubbleTheirs: {
    backgroundColor: colors.surface,
    ...hairline,
  },
  bubbleMine: {
    backgroundColor: colors.surfaceInverse,
  },
  stamp: {
    alignSelf: 'flex-end',
    marginTop: 2,
  },

  composer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: spacing.sm,
    paddingHorizontal: screenPadding,
    paddingVertical: spacing.md,
    backgroundColor: colors.surface,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  input: {
    flex: 1,
    ...inputText,
    maxHeight: 96,
    minHeight: 40,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: radii.md,
    backgroundColor: colors.surfaceMuted,
  },
  send: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.primary,
  },
  sendOff: {
    backgroundColor: colors.borderStrong,
  },

  empty: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.xxxl,
    paddingBottom: 120,
  },
  pressed: {
    opacity: 0.6,
  },
});

export default ChatScreen;
